import React, { useContext, useState } from "react";
import ValetApi from "../api/Api";
import UserContext from "../auth/UserContext";
import { parseTimestamp } from "../utility/parseTimestamp";
import { formatPhone } from "../utility/formatPhone";
import { getDateInputValueInTimeZone } from "../utility/dateInput";
import DamageReportReview from "../transactions/DamageReportReview";
import "./Graph.css";

const DATE_KEYS = ["transactionTime", "updatedAt", "createdAt"];
const OMIT_KEYS = ["podiumId", "locationId", "statusId", "userPhone", "updatedAt", "userId", "vehicleId", "vehicleStatusId", "username", "email"];

function formatHeaderLabel(key) {
    const trimmedKey = String(key || "").replace(/^(transaction|vehicle)/i, "");
    const fallbackKey = trimmedKey || String(key || "");
    const withSpaces = fallbackKey.replace(/([a-z])([A-Z])/g, "$1 $2");

    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

const Graph = () => {
    const { userDetail } = useContext(UserContext);
    const accessiblePodiums = userDetail?.accessiblePodiums || [];
    const userTimeZone = userDetail?.userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const todayInUserTimeZone = getDateInputValueInTimeZone(new Date(), userTimeZone);
    const [selectedPodiumId, setSelectedPodiumId] = useState(accessiblePodiums[0]?.id || "");
    const [startDate, setStartDate] = useState(todayInUserTimeZone);
    const [endDate, setEndDate] = useState(todayInUserTimeZone);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: "transactionTime", direction: "asc" });
    const [damageReportModal, setDamageReportModal] = useState({
        open: false,
        report: null,
        isLoading: false,
        error: "",
    });

    // Fetch data
    async function handleFetch(e) {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setTableData([]);
        try {
            const res = await ValetApi.getAllByPodiumIdDateRange(selectedPodiumId, startDate, endDate);
            setTableData(res.data.transactions || []);
        } catch (err) {
            setError("Could not fetch data.");
        }
        setLoading(false);
    }

    // Dynamically get all column keys from the first row of data, omitting specified keys
    const allKeys = React.useMemo(() => {
        if (tableData.length === 0) return [];
        return Object.keys(tableData[0]).filter((key) => !OMIT_KEYS.includes(key));
    }, [tableData]);

    // Sorting logic
    function handleSort(key) {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    }

    function getSortedData() {
        const sorted = [...tableData];
        sorted.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            // For date/time, sort as date
            if (DATE_KEYS.includes(sortConfig.key)) {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }
            if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }

    // Export to CSV
    function handleExportCSV() {
        if (!allKeys.length || !tableData.length) return;
        const rows = [
            allKeys.join(","), // header
            ...getSortedData().map((row) =>
                allKeys
                    .map((key) => {
                        let val = row[key];
                        if (DATE_KEYS.includes(key)) val = parseTimestamp(val);
                        if (typeof val === "string") {
                            // Escape quotes and commas
                            val = `"${val.replace(/"/g, '""')}"`;
                        }
                        return val;
                    })
                    .join(","),
            ),
        ];
        const csvContent = rows.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `transactions_${selectedPodiumId}_${startDate}_${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    function getApiErrorMessage(err, fallback = "Unable to load damage report") {
        return err?.[0] || err?.response?.data?.error?.message || err?.response?.data?.error || err?.message || fallback;
    }

    async function handleOpenDamageReport(row) {
        const transactionId = Number(row?.transactionId || 0);
        if (!transactionId || damageReportModal.isLoading) return;

        setDamageReportModal({ open: true, report: null, isLoading: true, error: "" });

        try {
            const res = await ValetApi.getTransactionDamagesReport(transactionId);
            const report = res?.data?.report || null;

            setDamageReportModal({
                open: true,
                report,
                isLoading: false,
                error: report ? "" : "No damage report available for this transaction.",
            });
        } catch (err) {
            setDamageReportModal({
                open: true,
                report: null,
                isLoading: false,
                error: getApiErrorMessage(err, "Unable to load damage report"),
            });
        }
    }

    function handleCloseDamageReport() {
        setDamageReportModal({ open: false, report: null, isLoading: false, error: "" });
    }

    return (
        <div className="graph-container graph-container-wide">
            <div className="graph-form-sticky">
                <h2 className="graph-title">Podium Transactions Table</h2>
                <form
                    className="graph-form"
                    onSubmit={handleFetch}>
                    <label>
                        Podium:
                        <select
                            className="graph-select"
                            value={selectedPodiumId}
                            onChange={(e) => setSelectedPodiumId(e.target.value)}>
                            {accessiblePodiums.map((p) => (
                                <option
                                    key={p.id}
                                    value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Start Date:
                        <input
                            type="date"
                            className="graph-date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </label>
                    <label>
                        End Date:
                        <input
                            type="date"
                            className="graph-date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </label>
                    <button
                        className="graph-btn"
                        type="submit"
                        disabled={loading}>
                        {loading ? "Loading..." : "Fetch"}
                    </button>
                    <button
                        className="graph-btn graph-btn-export"
                        type="button"
                        onClick={handleExportCSV}
                        disabled={!allKeys.length || !tableData.length}>
                        Export to CSV
                    </button>
                </form>
            </div>
            {error && <div className="graph-error">{error}</div>}
            <div className="graph-table-container graph-table-container-wide">
                <div className="graph-table-header-wrap">
                    <table className="graph-table graph-table-header-only">
                        <thead>
                            <tr>
                                {allKeys.map((key) => (
                                    <th
                                        key={key}
                                        onClick={() => handleSort(key)}
                                        className="graph-table-header">
                                        {formatHeaderLabel(key)}
                                        {sortConfig.key === key ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : ""}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                    </table>
                </div>

                <div className="graph-table-body-scroll">
                    <table className="graph-table graph-table-body-only">
                        <tbody>
                            {getSortedData().map((row, idx) => (
                                <tr key={idx}>
                                    {allKeys.map((key) => {
                                        if (key === "vehicleDamages") {
                                            let damages = [];
                                            try {
                                                damages = JSON.parse(row[key] || "[]");
                                            } catch {
                                                damages = [];
                                            }

                                            const canOpenReport = damages.length > 0 && Number(row?.transactionId || 0) > 0;

                                            return (
                                                <td
                                                    key={key}
                                                    data-label={formatHeaderLabel(key)}>
                                                    <button
                                                        className="damage-count-btn"
                                                        onClick={() => handleOpenDamageReport(row)}
                                                        disabled={!canOpenReport}>
                                                        {damages.length}
                                                    </button>
                                                </td>
                                            );
                                        }

                                        let displayValue = row[key];

                                        if (DATE_KEYS.includes(key)) {
                                            displayValue = parseTimestamp(displayValue);
                                        } else if (key === "vehicleMobile") {
                                            displayValue = formatPhone(displayValue);
                                        } else if (displayValue == null) {
                                            displayValue = "";
                                        } else {
                                            displayValue = String(displayValue);
                                        }

                                        if (typeof displayValue === "string" && displayValue.length > 100) {
                                            return (
                                                <td
                                                    key={key}
                                                    data-label={formatHeaderLabel(key)}>
                                                    <span title={displayValue}>{displayValue.slice(0, 100)}...</span>
                                                </td>
                                            );
                                        }

                                        return (
                                            <td
                                                key={key}
                                                data-label={formatHeaderLabel(key)}>
                                                {displayValue}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                            {tableData.length === 0 && !loading && (
                                <tr>
                                    <td
                                        className="graph-no-data-cell"
                                        colSpan={Math.max(allKeys.length, 1)}
                                        style={{ textAlign: "center", color: "#888" }}>
                                        No data found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {damageReportModal.open && (
                <div
                    className="damage-modal-overlay"
                    onClick={handleCloseDamageReport}>
                    <div
                        className="damage-modal damage-report-modal-shell"
                        onClick={(e) => e.stopPropagation()}>
                        <button
                            className="damage-modal-close"
                            onClick={handleCloseDamageReport}>
                            ×
                        </button>
                        <h3>Damage Report Review</h3>
                        <DamageReportReview
                            report={damageReportModal.report}
                            isLoading={damageReportModal.isLoading}
                            error={damageReportModal.error}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Graph;
